import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { WorkspaceSyncStatus } from "@/types";

// #229: Workspace-Sync-Mutationen (User-Gerät freischalten/entfernen, Richtung setzen).
// Alle invalidieren die workspace-sync-Sub-Resource UND die Agent-Ressource (deren
// Kurzsicht agent.syncthing.enabled mitzieht).

function useSyncClient() {
  const { session, logout } = useAuth();
  return () =>
    createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
}

function useInvalidate(agentId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: queryKeys.subResource("agents", agentId, "workspace-sync") });
    qc.invalidateQueries({ queryKey: queryKeys.resource("agents") });
  };
}

export function useAddSyncDevice(
  agentId: string,
): UseMutationResult<WorkspaceSyncStatus, Error, { deviceId: string; label: string }> {
  const getClient = useSyncClient();
  const invalidate = useInvalidate(agentId);
  return useMutation({
    mutationFn: ({ deviceId, label }) =>
      getClient().post<WorkspaceSyncStatus>(`/agents/${agentId}/workspace/sync/devices`, { deviceId, label }),
    onSuccess: invalidate,
  });
}

export function useRemoveSyncDevice(
  agentId: string,
): UseMutationResult<WorkspaceSyncStatus, Error, string> {
  const getClient = useSyncClient();
  const invalidate = useInvalidate(agentId);
  return useMutation({
    mutationFn: (deviceId) =>
      getClient().delete<WorkspaceSyncStatus>(`/agents/${agentId}/workspace/sync/devices/${encodeURIComponent(deviceId)}`),
    onSuccess: invalidate,
  });
}

// #229: Sync-Identität (re)provisionieren — für Bestands-Agents, die vor dem Feature
// erstellt wurden (noch keine Identität). Recreatet den Brain falls laufend. Liefert den
// Agent zurück; die Section invalidiert + refetcht den Sync-Status (dann available:true).
export function useProvisionSync(agentId: string): UseMutationResult<unknown, Error, void> {
  const getClient = useSyncClient();
  const invalidate = useInvalidate(agentId);
  return useMutation({
    mutationFn: () => getClient().post<unknown>(`/agents/${agentId}/workspace/sync/provision`),
    onSuccess: invalidate,
  });
}

// #229: Ignore-Patterns (.stignore) setzen — was NICHT gesynct wird (node_modules etc.).
export function useSetSyncIgnore(
  agentId: string,
): UseMutationResult<WorkspaceSyncStatus, Error, string[]> {
  const getClient = useSyncClient();
  const invalidate = useInvalidate(agentId);
  return useMutation({
    mutationFn: (patterns) =>
      getClient().put<WorkspaceSyncStatus>(`/agents/${agentId}/workspace/sync/ignore`, { patterns }),
    onSuccess: invalidate,
  });
}

export function useDisableSync(
  agentId: string,
): UseMutationResult<WorkspaceSyncStatus, Error, void> {
  const getClient = useSyncClient();
  const invalidate = useInvalidate(agentId);
  return useMutation({
    mutationFn: () => getClient().delete<WorkspaceSyncStatus>(`/agents/${agentId}/workspace/sync`),
    onSuccess: invalidate,
  });
}

export function useSetSyncFolderType(
  agentId: string,
): UseMutationResult<WorkspaceSyncStatus, Error, "sendreceive" | "receiveonly" | "sendonly"> {
  const getClient = useSyncClient();
  const invalidate = useInvalidate(agentId);
  return useMutation({
    mutationFn: (folderType) =>
      getClient().put<WorkspaceSyncStatus>(`/agents/${agentId}/workspace/sync/folder-type`, { folderType }),
    onSuccess: invalidate,
  });
}
